import React, { useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { 
  AdvancedFieldConfig, 
  AdvancedSectionConfig,
  AdvancedBodyConfig,
  type FontSize,
  type BodyLayout,
  type CardCustomizationController
} from '@/shared/hooks/useCardCustomization'

interface FieldOption {
  key: string
  label: string
}

interface AdvancedCardConfigModalProps {
  isOpen: boolean
  onClose: () => void
  controller: CardCustomizationController
  availableFields: FieldOption[]
}

export function AdvancedCardConfigModal({
  isOpen,
  onClose,
  controller,
  availableFields
}: AdvancedCardConfigModalProps) {
  const advancedConfig = controller.settings.advanced
  const isEnabled = advancedConfig?.enabled || false

  // Estados locais para edição (cópia do config atual)
  const [localTitle, setLocalTitle] = useState<AdvancedSectionConfig>(
    advancedConfig?.title || {
      type: 'single',
      fields: [{ fieldKey: availableFields[0]?.key || '' }],
      fontSize: 'lg',
      bold: true
    }
  )

  const [localSubtitle, setLocalSubtitle] = useState<AdvancedSectionConfig>(
    advancedConfig?.subtitle || {
      type: 'single',
      fields: [{ fieldKey: availableFields[1]?.key || '' }],
      fontSize: 'sm'
    }
  )

  const [localBadge, setLocalBadge] = useState<{
    fieldKey: string
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  } | null>(
    advancedConfig?.badge || null
  )

  const [localBody, setLocalBody] = useState<AdvancedBodyConfig>(
    advancedConfig?.body || {
      layout: 'list',
      fields: availableFields.map(f => ({
        key: f.key,
        visible: true,
        showLabel: true
      }))
    }
  )

  const [draggedField, setDraggedField] = useState<string | null>(null)

  // Presets rápidos
  const presets = [
    {
      name: 'E-commerce',
      icon: '🛍️',
      description: 'Nome do produto + Preço',
      config: {
        title: {
          type: 'single' as const,
          fields: [{ fieldKey: availableFields[0]?.key || '' }],
          fontSize: 'lg' as FontSize,
          bold: true
        },
        subtitle: {
          type: 'combined' as const,
          fields: [
            { fieldKey: availableFields[1]?.key || '', prefix: 'R$ ' },
            { fieldKey: availableFields[2]?.key || '', prefix: ' | ', separator: '' }
          ],
          fontSize: 'sm' as FontSize
        }
      }
    },
    {
      name: 'Dashboard',
      icon: '📊',
      description: 'Categoria | ID + Status',
      config: {
        title: {
          type: 'combined' as const,
          fields: [
            { fieldKey: availableFields[0]?.key || '' },
            { fieldKey: availableFields[1]?.key || '', separator: ' | ' }
          ],
          fontSize: 'lg' as FontSize,
          bold: true
        },
        subtitle: {
          type: 'single' as const,
          fields: [{ fieldKey: availableFields[2]?.key || '' }],
          fontSize: 'xs' as FontSize
        }
      }
    },
    {
      name: 'Contatos',
      icon: '👤',
      description: 'Nome + Email',
      config: {
        title: {
          type: 'single' as const,
          fields: [{ fieldKey: availableFields[0]?.key || '' }],
          fontSize: 'base' as FontSize,
          bold: true
        },
        subtitle: {
          type: 'single' as const,
          fields: [{ fieldKey: availableFields[1]?.key || '', prefix: '✉️ ' }],
          fontSize: 'xs' as FontSize
        }
      }
    }
  ]

  const applyPreset = (preset: typeof presets[0]) => {
    setLocalTitle(preset.config.title)
    setLocalSubtitle(preset.config.subtitle)
  }

  // Salvar configurações
  const handleSave = () => {
    if (!isEnabled) {
      controller.enableAdvancedMode()
    }
    controller.setAdvancedTitle(localTitle)
    controller.setAdvancedSubtitle(localSubtitle)
    controller.setAdvancedBadge(localBadge)
    controller.setAdvancedBody(localBody)
    onClose()
  }

  // Desativar modo avançado
  const handleDisable = () => {
    controller.disableAdvancedMode()
    onClose()
  }

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, fieldKey: string) => {
    setDraggedField(fieldKey)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  const handleDropOnTitle = (e: React.DragEvent) => {
    e.preventDefault()
    if (!draggedField) return

    if (localTitle.type === 'single') {
      // Substituir campo único
      setLocalTitle({
        ...localTitle,
        fields: [{ fieldKey: draggedField }]
      })
    } else {
      // Adicionar ao final em modo combinado
      setLocalTitle({
        ...localTitle,
        fields: [...localTitle.fields, { fieldKey: draggedField, separator: ' | ' }]
      })
    }
    setDraggedField(null)
  }

  const handleDropOnSubtitle = (e: React.DragEvent) => {
    e.preventDefault()
    if (!draggedField) return

    if (localSubtitle.type === 'single') {
      setLocalSubtitle({
        ...localSubtitle,
        fields: [{ fieldKey: draggedField }]
      })
    } else {
      setLocalSubtitle({
        ...localSubtitle,
        fields: [...localSubtitle.fields, { fieldKey: draggedField, separator: ' | ' }]
      })
    }
    setDraggedField(null)
  }

  const handleDropOnBadge = (e: React.DragEvent) => {
    e.preventDefault()
    if (!draggedField) return

    setLocalBadge({
      fieldKey: draggedField,
      variant: localBadge?.variant || 'primary'
    })
    setDraggedField(null)
  }

  // Remover campo de uma seção
  const removeFieldFromTitle = (index: number) => {
    const newFields = localTitle.fields.filter((_, i) => i !== index)
    if (newFields.length === 0) {
      // Manter pelo menos um campo vazio
      setLocalTitle({ ...localTitle, fields: [{ fieldKey: '' }] })
    } else {
      setLocalTitle({ ...localTitle, fields: newFields })
    }
  }

  const removeFieldFromSubtitle = (index: number) => {
    const newFields = localSubtitle.fields.filter((_, i) => i !== index)
    if (newFields.length === 0) {
      setLocalSubtitle({ ...localSubtitle, fields: [{ fieldKey: '' }] })
    } else {
      setLocalSubtitle({ ...localSubtitle, fields: newFields })
    }
  }

  // Atualizar propriedades de campo
  const updateTitleField = (index: number, updates: Partial<AdvancedFieldConfig>) => {
    const newFields = [...localTitle.fields]
    newFields[index] = { ...newFields[index], ...updates }
    setLocalTitle({ ...localTitle, fields: newFields })
  }

  const updateSubtitleField = (index: number, updates: Partial<AdvancedFieldConfig>) => {
    const newFields = [...localSubtitle.fields]
    newFields[index] = { ...newFields[index], ...updates }
    setLocalSubtitle({ ...localSubtitle, fields: newFields })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="⚙️ Configuração Avançada de Cards"
      size="xl"
    >
      <div className="space-y-6">
        {/* Status */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              Modo Avançado {isEnabled ? 'Ativo' : 'Desativado'}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {isEnabled 
                ? 'Você pode combinar campos e personalizar completamente seus cards' 
                : 'Ative para customizar título, subtítulo e badge com campos combinados'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isEnabled && (
              <button
                onClick={handleDisable}
                className="px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                Desativar
              </button>
            )}
          </div>
        </div>

        {/* Preview ao Vivo */}
        <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview ao Vivo
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
            {/* Simulação de Card */}
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div className="flex-1">
                {/* Preview do Título */}
                <h4 
                  className={`${controller.getFontSizeClass(localTitle.fontSize || 'lg')} ${localTitle.bold ? 'font-bold' : 'font-semibold'} ${localTitle.italic ? 'italic' : ''}`}
                  style={{ color: localTitle.color || undefined }}
                >
                  {localTitle.fields.length > 0 && localTitle.fields[0].fieldKey ? (
                    localTitle.type === 'single' ? (
                      <>
                        {localTitle.fields[0].prefix || ''}
                        {availableFields.find(f => f.key === localTitle.fields[0].fieldKey)?.label || 'Campo'}
                        {localTitle.fields[0].suffix || ''}
                      </>
                    ) : (
                      localTitle.fields.map((field, idx) => (
                        <React.Fragment key={idx}>
                          {field.prefix || ''}
                          {availableFields.find(f => f.key === field.fieldKey)?.label || 'Campo'}
                          {field.suffix || ''}
                          {idx < localTitle.fields.length - 1 ? (field.separator || '') : ''}
                        </React.Fragment>
                      ))
                    )
                  ) : (
                    'Título do Card'
                  )}
                </h4>
                {/* Preview do Subtítulo */}
                {localSubtitle.fields.length > 0 && localSubtitle.fields[0].fieldKey && (
                  <p 
                    className={`${controller.getFontSizeClass(localSubtitle.fontSize || 'sm')} ${localSubtitle.bold ? 'font-bold' : ''} ${localSubtitle.italic ? 'italic' : ''}`}
                    style={{ color: localSubtitle.color || undefined }}
                  >
                    {localSubtitle.type === 'single' ? (
                      <>
                        {localSubtitle.fields[0].prefix || ''}
                        {availableFields.find(f => f.key === localSubtitle.fields[0].fieldKey)?.label || 'Campo'}
                        {localSubtitle.fields[0].suffix || ''}
                      </>
                    ) : (
                      localSubtitle.fields.map((field, idx) => (
                        <React.Fragment key={idx}>
                          {field.prefix || ''}
                          {availableFields.find(f => f.key === field.fieldKey)?.label || 'Campo'}
                          {field.suffix || ''}
                          {idx < localSubtitle.fields.length - 1 ? (field.separator || '') : ''}
                        </React.Fragment>
                      ))
                    )}
                  </p>
                )}
              </div>
              {/* Preview do Badge */}
              {localBadge && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  localBadge.variant === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' :
                  localBadge.variant === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                  localBadge.variant === 'danger' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                  localBadge.variant === 'info' ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300' :
                  'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                }`}>
                  {availableFields.find(f => f.key === localBadge.fieldKey)?.label || 'Badge'}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 italic text-center">
              ✨ Esta é uma prévia de como seu card ficará
            </p>
          </div>
        </div>

        {/* Layout com 2 colunas: Paleta de Campos | Áreas de Drop */}
        <div className="grid grid-cols-3 gap-6">
          {/* Paleta de Campos (1 coluna) */}
          <div className="col-span-1">
            <div className="sticky top-4 space-y-4">
              {/* Presets Rápidos */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Presets Rápidos
                </h3>
                <div className="space-y-2">
                  {presets.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className="w-full p-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-md transition-all text-left group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{preset.icon}</span>
                        <span className="text-sm font-medium text-slate-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                          {preset.name}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 ml-7">
                        {preset.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Campos Disponíveis */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  Campos Disponíveis
                </h3>
                <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border border-slate-200 dark:border-slate-700">
                  {availableFields.map((field) => (
                    <div
                      key={field.key}
                      draggable
                      onDragStart={(e) => handleDragStart(e, field.key)}
                      className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-move hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-md transition-all group"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-slate-400 group-hover:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                        <span className="text-sm font-medium text-slate-900 dark:text-white">{field.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">
                        Arraste para título, subtítulo ou badge
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Áreas de Configuração (2 colunas) */}
          <div className="col-span-2 space-y-4">
            {/* Título */}
            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  Título
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={localTitle.type}
                    onChange={(e) => setLocalTitle({ ...localTitle, type: e.target.value as 'single' | 'combined' })}
                    className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                  >
                    <option value="single">Campo Único</option>
                    <option value="combined">Campos Combinados</option>
                  </select>
                  <select
                    value={localTitle.fontSize || 'lg'}
                    onChange={(e) => setLocalTitle({ ...localTitle, fontSize: e.target.value as FontSize })}
                    className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                  >
                    <option value="xs">XS</option>
                    <option value="sm">SM</option>
                    <option value="base">Base</option>
                    <option value="lg">LG</option>
                    <option value="xl">XL</option>
                  </select>
                  {/* Bold Toggle */}
                  <button
                    onClick={() => setLocalTitle({ ...localTitle, bold: !localTitle.bold })}
                    className={`px-2 py-1 text-xs font-bold rounded transition-colors ${
                      localTitle.bold 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                    title="Negrito"
                  >
                    B
                  </button>
                  {/* Italic Toggle */}
                  <button
                    onClick={() => setLocalTitle({ ...localTitle, italic: !localTitle.italic })}
                    className={`px-2 py-1 text-xs italic rounded transition-colors ${
                      localTitle.italic 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                    title="Itálico"
                  >
                    I
                  </button>
                  {/* Color Picker */}
                  <input
                    type="color"
                    value={localTitle.color || '#000000'}
                    onChange={(e) => setLocalTitle({ ...localTitle, color: e.target.value })}
                    className="w-8 h-6 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                    title="Cor do texto"
                  />
                </div>
              </div>

              <div
                onDragOver={handleDragOver}
                onDrop={handleDropOnTitle}
                className="min-h-[100px] p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600"
              >
                {localTitle.fields.length === 0 || !localTitle.fields[0].fieldKey ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
                    <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm">Arraste um campo aqui</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {localTitle.fields.map((field, index) => {
                      const fieldOption = availableFields.find(f => f.key === field.fieldKey)
                      return (
                        <div key={index} className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                          <span className="text-sm font-medium text-slate-900 dark:text-white flex-1">
                            {fieldOption?.label || field.fieldKey}
                          </span>
                          {localTitle.type === 'combined' && (
                            <input
                              type="text"
                              value={field.separator || ''}
                              onChange={(e) => updateTitleField(index, { separator: e.target.value })}
                              placeholder="Separador"
                              className="w-16 text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                            />
                          )}
                          <button
                            onClick={() => removeFieldFromTitle(index)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Subtítulo */}
            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                  Subtítulo <span className="text-xs text-slate-500">(opcional)</span>
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={localSubtitle.type}
                    onChange={(e) => setLocalSubtitle({ ...localSubtitle, type: e.target.value as 'single' | 'combined' })}
                    className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                  >
                    <option value="single">Campo Único</option>
                    <option value="combined">Campos Combinados</option>
                  </select>
                  <select
                    value={localSubtitle.fontSize || 'sm'}
                    onChange={(e) => setLocalSubtitle({ ...localSubtitle, fontSize: e.target.value as FontSize })}
                    className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                  >
                    <option value="xs">XS</option>
                    <option value="sm">SM</option>
                    <option value="base">Base</option>
                    <option value="lg">LG</option>
                    <option value="xl">XL</option>
                  </select>
                  {/* Bold Toggle */}
                  <button
                    onClick={() => setLocalSubtitle({ ...localSubtitle, bold: !localSubtitle.bold })}
                    className={`px-2 py-1 text-xs font-bold rounded transition-colors ${
                      localSubtitle.bold 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                    title="Negrito"
                  >
                    B
                  </button>
                  {/* Italic Toggle */}
                  <button
                    onClick={() => setLocalSubtitle({ ...localSubtitle, italic: !localSubtitle.italic })}
                    className={`px-2 py-1 text-xs italic rounded transition-colors ${
                      localSubtitle.italic 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                    title="Itálico"
                  >
                    I
                  </button>
                  {/* Color Picker */}
                  <input
                    type="color"
                    value={localSubtitle.color || '#6B7280'}
                    onChange={(e) => setLocalSubtitle({ ...localSubtitle, color: e.target.value })}
                    className="w-8 h-6 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                    title="Cor do texto"
                  />
                </div>
              </div>

              <div
                onDragOver={handleDragOver}
                onDrop={handleDropOnSubtitle}
                className="min-h-[80px] p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600"
              >
                {localSubtitle.fields.length === 0 || !localSubtitle.fields[0].fieldKey ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
                    <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs">Arraste um campo aqui</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {localSubtitle.fields.map((field, index) => {
                      const fieldOption = availableFields.find(f => f.key === field.fieldKey)
                      return (
                        <div key={index} className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                          <span className="text-sm font-medium text-slate-900 dark:text-white flex-1">
                            {fieldOption?.label || field.fieldKey}
                          </span>
                          {localSubtitle.type === 'combined' && (
                            <input
                              type="text"
                              value={field.separator || ''}
                              onChange={(e) => updateSubtitleField(index, { separator: e.target.value })}
                              placeholder="Separador"
                              className="w-16 text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                            />
                          )}
                          <button
                            onClick={() => removeFieldFromSubtitle(index)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Badge */}
            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Badge <span className="text-xs text-slate-500">(opcional)</span>
                </h3>
                {localBadge && (
                  <div className="flex items-center gap-2">
                    <select
                      value={localBadge.variant || 'primary'}
                      onChange={(e) => setLocalBadge({ ...localBadge, variant: e.target.value as any })}
                      className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                    >
                      <option value="primary">Primary</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="danger">Danger</option>
                      <option value="info">Info</option>
                    </select>
                    <button
                      onClick={() => setLocalBadge(null)}
                      className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div
                onDragOver={handleDragOver}
                onDrop={handleDropOnBadge}
                className="min-h-[60px] p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center"
              >
                {!localBadge ? (
                  <div className="flex flex-col items-center text-slate-400 dark:text-slate-500">
                    <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs">Arraste um campo aqui</span>
                  </div>
                ) : (
                  <div className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {availableFields.find(f => f.key === localBadge.fieldKey)?.label || localBadge.fieldKey}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Corpo do Card (Body/Fields) */}
            <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  Corpo do Card (Campos)
                </h3>
                <select
                  value={localBody.layout}
                  onChange={(e) => setLocalBody({ ...localBody, layout: e.target.value as BodyLayout })}
                  className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                >
                  <option value="list">Lista Vertical</option>
                  <option value="grid-2">Grid 2 Colunas</option>
                  <option value="inline">Inline Horizontal</option>
                </select>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {localBody.fields.map((field, index) => {
                  const fieldOption = availableFields.find(f => f.key === field.key)
                  return (
                    <div key={field.key} className="p-3 bg-slate-50 dark:bg-slate-900/30 rounded-lg border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={field.visible}
                            onChange={(e) => {
                              const newFields = [...localBody.fields]
                              newFields[index].visible = e.target.checked
                              setLocalBody({ ...localBody, fields: newFields })
                            }}
                            className="w-4 h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500"
                          />
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {fieldOption?.label || field.key}
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={field.showLabel !== false}
                          onChange={(e) => {
                            const newFields = [...localBody.fields]
                            newFields[index].showLabel = e.target.checked
                            setLocalBody({ ...localBody, fields: newFields })
                          }}
                          className="w-4 h-4 text-blue-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-500"
                          title="Mostrar label"
                        />
                        <span className="text-xs text-slate-500">Mostrar label</span>
                      </div>

                      {field.visible && (
                        <div className="flex items-center gap-2 ml-6 flex-wrap">
                          {/* Custom Label */}
                          <input
                            type="text"
                            value={field.label || ''}
                            onChange={(e) => {
                              const newFields = [...localBody.fields]
                              newFields[index].label = e.target.value
                              setLocalBody({ ...localBody, fields: newFields })
                            }}
                            placeholder={`Label: ${fieldOption?.label || field.key}`}
                            className="flex-1 min-w-[120px] text-xs px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                          />

                          {/* Font Size */}
                          <select
                            value={field.fontSize || 'sm'}
                            onChange={(e) => {
                              const newFields = [...localBody.fields]
                              newFields[index].fontSize = e.target.value as FontSize
                              setLocalBody({ ...localBody, fields: newFields })
                            }}
                            className="text-xs px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded"
                          >
                            <option value="xs">XS</option>
                            <option value="sm">SM</option>
                            <option value="base">Base</option>
                            <option value="lg">LG</option>
                          </select>

                          {/* Bold Toggle */}
                          <button
                            onClick={() => {
                              const newFields = [...localBody.fields]
                              newFields[index].bold = !newFields[index].bold
                              setLocalBody({ ...localBody, fields: newFields })
                            }}
                            className={`px-2 py-1 text-xs font-bold rounded transition-colors ${
                              field.bold 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                            }`}
                            title="Negrito"
                          >
                            B
                          </button>

                          {/* Color Picker */}
                          <input
                            type="color"
                            value={field.color || '#374151'}
                            onChange={(e) => {
                              const newFields = [...localBody.fields]
                              newFields[index].color = e.target.value
                              setLocalBody({ ...localBody, fields: newFields })
                            }}
                            className="w-8 h-6 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                            title="Cor do texto"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 rounded-lg transition-all shadow-lg hover:shadow-xl"
          >
            💾 Salvar Configuração
          </button>
        </div>
      </div>
    </Modal>
  )
}
